import {
  hashKey,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import type { CronJobStatus, UpdateCronJobConfigDto } from '@brickup/shared/dto'
import type { GlanceOptions } from './glance'
import { apiClient } from '@/api/client'
import { useMutationWithInvalidation } from '@/hooks/use-mutation-with-invalidation'

/**
 * The hooks behind a scheduled job's settings row: its status, read again
 * every few seconds while a run is going, the switch, and "run now". Built
 * once, so every row behaves alike — the run shows as going the moment it is
 * pressed, and a failed write says so.
 *
 * `key` heads every query of the feature, so a write refreshes the feature's
 * other reads as well; `routes` are where the job's status, switch and run
 * are served. `pushed` says whether a socket is reporting the job's runs at
 * the moment; while it is, the status is not polled during a run.
 */
export function cronJobApi<
  Status extends CronJobStatus,
  Result,
  Update extends UpdateCronJobConfigDto = UpdateCronJobConfigDto,
>(
  key: string,
  routes: { status: string; config: string; trigger: string },
  { pushed }: { pushed?: () => boolean } = {},
) {
  const statusKey = [key, 'config'] as const
  const statusHash = hashKey(statusKey)
  const fetchStatus = async () =>
    (await apiClient.get<Status>(routes.status)).data

  function useStatus({
    refetchInterval = false,
    ...options
  }: GlanceOptions = {}) {
    return useQuery({
      queryKey: statusKey,
      queryFn: fetchStatus,
      ...options,
      refetchInterval: query =>
        query.state.data?.running && !pushed?.() ? 5_000 : refetchInterval,
    })
  }

  function useMutations() {
    const queryClient = useQueryClient()
    const refresh = () => queryClient.invalidateQueries({ queryKey: [key] })

    // The write answers the status, so that entry is set from the answer and
    // only the feature's other reads are fetched again.
    const updateConfigMutation = useMutationWithInvalidation(
      async (dto: Update) =>
        (await apiClient.patch<Status>(routes.config, dto)).data,
      () =>
        queryClient.invalidateQueries({
          queryKey: [key],
          predicate: query => query.queryHash !== statusHash,
        }),
      { onSuccess: status => queryClient.setQueryData(statusKey, status) },
    )
    const triggerMutation = useMutation({
      mutationFn: async () =>
        (await apiClient.post<Result>(routes.trigger)).data,
      onMutate: () =>
        queryClient.setQueryData<Status>(statusKey, old =>
          old ? { ...old, running: true } : old,
        ),
      onSuccess: refresh,
      onError: refresh,
    })
    return { updateConfigMutation, triggerMutation }
  }

  // `statusKey` and `fetchStatus` are for a reader outside React — a socket
  // that learns of a run — so it writes into the entry the hooks read.
  return { useStatus, useMutations, statusKey, fetchStatus, routes }
}

/** The routes most jobs serve under their feature's path. */
export const cronJobRoutes = (base: string) => ({
  status: `${base}/config`,
  config: `${base}/config`,
  trigger: `${base}/trigger`,
})
